import React, { useEffect, useState } from 'react';
import { Card, Typography, Row, Col, Button, Divider, Table, Select, Tag } from 'antd';
import {
    AimOutlined, LineChartOutlined, TrophyOutlined, FilterOutlined,
    DollarOutlined, TeamOutlined, DownloadOutlined, FileDoneOutlined,
    CalendarOutlined, ExclamationCircleOutlined, ClockCircleOutlined,
    CheckCircleOutlined, PhoneOutlined,
} from '@ant-design/icons';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid,
    PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip, Legend,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { EnquiryService } from '../../Services/Manage/EnquiryService';
import { SalesTargetService } from '../../Services/Manage/SalesTargetService';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isBetween from 'dayjs/plugin/isBetween';
import { AddEmployeesService } from '../../Services/Manage/AddEmployeesService';
import { InvoiceService } from '../../Services/Manage/InvoiceService';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(isBetween);

const inr = (n) => `₹ ${Number(n || 0).toLocaleString('en-IN')}`;

const getPermission = (link) => {
    try {
        const raw = localStorage.getItem("ome_permission");
        const parsed = JSON.parse(raw);
        const permissions = Array.isArray(parsed) ? parsed : JSON.parse(parsed);
        return permissions.find(p => p.link === link) || { add: false, edit: false, delete: false, read: false };
    } catch { return { add: false, edit: false, delete: false, read: false }; }
};

const SalesDashboard = () => {
    const perm = getPermission('/sales-dashboard');
    const viewAll = perm.view_all === true;
    const navigate = useNavigate();
    const currentUser = JSON.parse(localStorage.getItem("ome_user") || "{}");
    const currentUserName = currentUser?.name || currentUser?.user_name || "";
    const [enquiries, setEnquiries] = useState([]);
    const [targets, setTargets] = useState([]);
    const [allStatusConfig, setAllStatusConfig] = useState([]);
    const [wonStatuses, setWonStatuses] = useState([]);
    const [period, setPeriod] = useState('Monthly');
    const [quotations, setQuotations] = useState([]);
    const [userMap, setUserMap] = useState({});
    const [salesExecNames, setSalesExecNames] = useState([]);
    const [invoices, setInvoices] = useState([]);

    useEffect(() => {
        loadEnquiries();
        loadTargets();
        loadStatuses();
        loadQuotations();
        loadInvoices();
        loadUserMap();
        loadSalesExecutives();
    }, []);

    const loadInvoices = async () => {
        try {
            const res = await InvoiceService.getAllInvoice();
            setInvoices(res.data || []);
        } catch (e) { console.error(e); }
    };

    const loadQuotations = async () => {
        try {
            const { QuotationService } = await import('../../Services/Manage/QuotationService');
            const res = await QuotationService.getAllQuotation();
            setQuotations(res.data || []);
        } catch (e) { console.error(e); }
    };

    const loadUserMap = async () => {
        try {
            const { UserService } = await import('../../Services/Manage/UserService');
            const res = await UserService.getAllUsers();
            const list = res?.data || res || [];
            const map = {};
            list.forEach((u) => {
                const uid = u.uid || u.user_id || u.id;
                const nm = u.name || u.user_name || u.username;
                if (uid && nm) map[String(uid).trim()] = nm;
            });
            setUserMap(map);
        } catch (e) { console.error(e); }
    };

    const loadSalesExecutives = async () => {
        try {
            const res = await AddEmployeesService.getByDepartment("Sales");
            const names = (res.data || []).map(e => e.name).filter(Boolean);
            setSalesExecNames(names);
        } catch (e) { console.error(e); }
    };

    const loadEnquiries = async () => {
        try {
            const res = await EnquiryService.getAllEnquiries();
            const all = res.data || [];
            if (viewAll) {
                setEnquiries(all);
            } else {
                const filtered = all.filter(d => {
                    const isAssigned = (d.assigned_to || '').split(',').map(s => s.trim()).includes(currentUserName);
                    const isCreator = String(d.addedby ?? '').trim() === String(currentUser?.uid ?? '').trim();
                    return isAssigned || isCreator;
                });
                setEnquiries(filtered);
            }
        }
        catch (e) { console.error(e); }
    };
    const loadTargets = async () => {
        try {
            const res = await SalesTargetService.getAll();
            const all = res.data || [];
            if (viewAll) {
                setTargets(all);
            } else {
                const filtered = all.filter(t => {
                    if (t.sales_executive_name) return t.sales_executive_name.trim() === currentUserName;
                    const mt = Array.isArray(t.member_targets) ? t.member_targets
                        : (() => { try { return JSON.parse(t.member_targets || '[]'); } catch { return []; } })();
                    return mt.some(m => (m.member_name || '').trim() === currentUserName);
                });
                setTargets(filtered);
            }
        }
        catch (e) { console.error(e); }
    };
    const loadStatuses = async () => {
        try {
            const { ConfigurationService } = await import('../../Services/Manage/ConfigurationService');
            const res = await ConfigurationService.getByType('enquiry_status');
            const all = res.data || [];
            setAllStatusConfig(all);
            setWonStatuses(all.filter(i => i.label.trim().toLowerCase() === 'won').map(i => i.label.trim()));
        } catch (e) { console.error(e); }
    };

    const parseItems = (items) => {
        if (Array.isArray(items)) return items;
        try { return JSON.parse(items || '[]'); } catch { return []; }
    };
    const amountOf = (d) => {
        let amt = parseFloat(String(d.total_amount ?? 0).replace(/[^0-9.-]/g, '')) || 0;
        if (!amt) {
            const s = parseItems(d.service_items);
            const p = parseItems(d.product_items);
            amt = [...s, ...p].reduce((a, it) => a + (Number(it.unit_price) || 0), 0);
        }
        return amt;
    };
    const isWon = (d) => wonStatuses.includes(d.eq_status?.trim());

    const revenueTarget = targets.reduce((sum, t) => {
        if (t.sales_executive_name) {
            if (viewAll || t.sales_executive_name.trim() === currentUserName) {
                return sum + Number(t.revenue_target || 0);
            }
            return sum;
        }
        const mt = Array.isArray(t.member_targets) ? t.member_targets
            : (() => { try { return JSON.parse(t.member_targets || '[]'); } catch { return []; } })();
        if (viewAll) {
            return sum + mt.reduce((a, m) => a + Number(m.revenue_target || 0), 0);
        }
        const myShare = mt.filter(m => (m.member_name || '').trim() === currentUserName)
            .reduce((a, m) => a + Number(m.revenue_target || 0), 0);
        return sum + myShare;
    }, 0);

    // Collected amount helpers — revenue is real money received (invoice payments),
    // not the quoted/invoiced face value. Won deals = quotations CONVERTED.
    // Each quotation is credited to exactly ONE owner (same rule as SalesExecReport.js):
    // first assignee in assigned_to, else creator via added_by, else linked
    // enquiry's first assignee. This guarantees no quotation is ever double-counted.
    const collectedOf = (inv) => (inv.payments || []).reduce((s, p) => s + Number(p.paid_amt || 0), 0);
    const invoicesForQuotation = (quNo) =>
        invoices.filter((inv) => inv.qu_no === quNo || inv.source_qu_no === quNo);
    const collectedForQuotations = (qList) =>
        qList.reduce((sum, q) => sum + invoicesForQuotation(q.qu_no).reduce((s, inv) => s + collectedOf(inv), 0), 0);

    const resolveQuotationOwner = (q) => {
        const names = (q.assigned_to || '').split(',').map(s => s.trim()).filter(Boolean);
        if (names.length > 0) return names[0];
        if (q.added_by) {
            const resolvedName = userMap[String(q.added_by).trim()];
            if (resolvedName) return resolvedName;
        }
        const linkedEnquiry = enquiries.find(d => d.eq_id === q.eq_no);
        const enquiryAssignees = (linkedEnquiry?.assigned_to || '').split(',').map(s => s.trim()).filter(Boolean);
        if (enquiryAssignees.length > 0) return enquiryAssignees[0];
        return null;
    };

    const convertedQuotationsAll = quotations.filter((q) => (q.quotation_status || '').toLowerCase() === 'converted');

    const convertedQuotationsForUser = convertedQuotationsAll
        .filter((q) => {
            if (viewAll) return true;
            return resolveQuotationOwner(q) === currentUserName;
        });
    const wonDeals = convertedQuotationsForUser.length;

    // Revenue is summed directly from invoices matched to the executive
    // (or all executives if viewAll), same logic as SalesExecReport.js —
    // not via quotation-to-invoice linking, which can miss/mismatch invoices.
    const calcCollectedRevenue = (name) => {
        return invoices
            .filter((inv) => {
                const names = (inv.sales_executive || "").split(",").map(s => s.trim()).filter(Boolean);
                if (names.includes(name)) return true;
                if (names.length === 0 && inv.added_by) {
                    const resolvedName = userMap[String(inv.added_by).trim()];
                    return resolvedName === name;
                }
                return false;
            })
            .reduce((sum, inv) => sum + collectedOf(inv), 0);
    };

    const calcCollectedRevenueInRange = (name, start, end) => {
        return invoices
            .filter((inv) => {
                const names = (inv.sales_executive || "").split(",").map(s => s.trim()).filter(Boolean);
                if (names.includes(name)) return true;
                if (names.length === 0 && inv.added_by) {
                    const resolvedName = userMap[String(inv.added_by).trim()];
                    return resolvedName === name;
                }
                return false;
            })
            .filter((inv) => {
                const d = dayjs(inv.invoice_date || inv.added_on);
                return d.isValid() && d.isSameOrAfter(start, 'day') && d.isSameOrBefore(end, 'day');
            })
            .reduce((sum, inv) => sum + collectedOf(inv), 0);
    };

    const calcCollectedRevenueAllInRange = (start, end) =>
        invoices
            .filter((inv) => {
                const d = dayjs(inv.invoice_date || inv.added_on);
                return d.isValid() && d.isSameOrAfter(start, 'day') && d.isSameOrBefore(end, 'day');
            })
            .reduce((sum, inv) => sum + collectedOf(inv), 0);

    const resolveInvoiceSource = (inv) => {
        const q = quotations.find(qq => qq.qu_no === inv.qu_no || qq.qu_no === inv.source_qu_no);
        if (q) {
            const linkedEnquiry = enquiries.find(d => d.eq_id === q.eq_no);
            if (linkedEnquiry?.eq_source) return linkedEnquiry.eq_source.trim();
        }
        return 'Others';
    };

    const achievedRevenue = viewAll
        ? invoices.reduce((sum, inv) => sum + collectedOf(inv), 0)
        : calcCollectedRevenue(currentUserName);
    const totalLeads = enquiries.length;
    const conversionRate = totalLeads ? ((wonDeals / totalLeads) * 100).toFixed(2) : '0.00';
    const avgDealValue = wonDeals ? Math.round(achievedRevenue / wonDeals) : 0;
    const overallAchievement = revenueTarget ? Math.min((achievedRevenue / revenueTarget) * 100, 100) : 0;

    const topStats = [
        { title: 'Revenue Target', value: inr(revenueTarget), sub: 'This Period', color: '#1677ff', bg: '#f0f7ff', icon: <AimOutlined />, link: () => navigate('/sales-target'), linkLabel: 'View Targets' },
        { title: 'Achieved Revenue', value: inr(achievedRevenue), sub: 'This Period', color: '#52c41a', bg: '#f6ffed', icon: <LineChartOutlined /> },
        { title: 'Won Deals', value: wonDeals, sub: 'This Period', color: '#722ed1', bg: '#f9f0ff', icon: <TrophyOutlined /> },
        { title: 'Conversion Rate', value: `${conversionRate}%`, sub: 'This Period', color: '#fa8c16', bg: '#fff7e6', icon: <FilterOutlined /> },
        { title: 'Avg. Deal Value', value: inr(avgDealValue), sub: 'This Period', color: '#eb2f96', bg: '#fff0f6', icon: <TeamOutlined /> },
        { title: 'Collection Received', value: inr(achievedRevenue), sub: 'This Period', color: '#13c2c2', bg: '#f0fffe', icon: <DollarOutlined /> },
    ];

    const revenueTrend = (() => {
        const months = [];
        for (let i = 5; i >= 0; i--) months.push(dayjs().subtract(i, 'month'));
        const monthlyTargetTotal = revenueTarget; // flat line; per-month target not modeled
        return months.map(m => {
            const start = m.startOf('month');
            const end = m.endOf('month');
            const achieved = viewAll
                ? calcCollectedRevenueAllInRange(start, end)
                : calcCollectedRevenueInRange(currentUserName, start, end);
            return { label: m.format('MMM YYYY'), achieved, target: monthlyTargetTotal };
        });
    })();

    // ---- Pipeline funnel ----
    // ---- Pipeline funnel ----
    const pipelineColors = ['#1677ff', '#13c2c2', '#fa8c16', '#722ed1', '#eb2f96', '#52c41a', '#f5222d'];
    const pipeline = allStatusConfig.map((s, i) => {
        const isWonLabel = wonStatuses.includes(s.label.trim());
        return {
            label: s.label,
            count: isWonLabel ? wonDeals : enquiries.filter(d => d.eq_status?.trim() === s.label.trim()).length,
            color: pipelineColors[i % pipelineColors.length],
        };
    });
    const pipelineMax = Math.max(...pipeline.map(p => p.count), 1);

    // ---- Revenue by Lead Source (donut, from converted quotations matched to their enquiry's source) ----
    // ---- Revenue by Lead Source (from invoices matched directly to the
    // executive — same rule as calcCollectedRevenue — attributed to a source
    // by tracing the invoice back through its quotation to that quotation's enquiry) ----
    const sourceColors = ['#1677ff', '#52c41a', '#fa8c16', '#722ed1', '#eb2f96', '#bfbfbf'];
    const matchedInvoicesForSource = invoices.filter((inv) => {
        if (viewAll) return true;
        const names = (inv.sales_executive || "").split(",").map(s => s.trim()).filter(Boolean);
        if (names.includes(currentUserName)) return true;
        if (names.length === 0 && inv.added_by) {
            return userMap[String(inv.added_by).trim()] === currentUserName;
        }
        return false;
    });
    const sourceMap = {};
    matchedInvoicesForSource.forEach(inv => {
        const src = resolveInvoiceSource(inv);
        sourceMap[src] = (sourceMap[src] || 0) + collectedOf(inv);
    });
    const sourceTotal = Object.values(sourceMap).reduce((a, b) => a + b, 0);
    const sourceData = Object.entries(sourceMap)
        .map(([name, value], i) => ({ name, value, pct: sourceTotal ? Math.round((value / sourceTotal) * 100) : 0, color: sourceColors[i % sourceColors.length] }))
        .sort((a, b) => b.value - a.value);

    // ---- Per-exec aggregation (top performers + leaderboard) ----
    const targetByName = {};
    targets.forEach((t) => {
        if (t.sales_executive_name) targetByName[t.sales_executive_name] = (targetByName[t.sales_executive_name] || 0) + Number(t.revenue_target || 0);
        else {
            const mt = Array.isArray(t.member_targets) ? t.member_targets
                : (() => { try { return JSON.parse(t.member_targets || '[]'); } catch { return []; } })();
            mt.forEach(m => { if (m.member_name) targetByName[m.member_name] = (targetByName[m.member_name] || 0) + Number(m.revenue_target || 0); });
        }
    });
    const execNamesSet = new Set();
    enquiries.forEach(d => (d.assigned_to || '').split(',').map(s => s.trim()).filter(Boolean).forEach(n => execNamesSet.add(n)));
    quotations.forEach(q => {
        const names = (q.assigned_to || '').split(',').map(s => s.trim()).filter(Boolean);
        names.forEach(n => execNamesSet.add(n));
        if (names.length === 0 && q.added_by) {
            const resolvedName = userMap[String(q.added_by).trim()];
            if (resolvedName && salesExecNames.includes(resolvedName)) execNamesSet.add(resolvedName);
        }
    });
    Object.keys(targetByName).forEach(n => execNamesSet.add(n));
    // Ensure every owner resolved for a CONVERTED quotation is represented in
    // execNamesSet — otherwise that quotation's collected revenue is counted in
    // achievedRevenue (which sums ALL converted quotations) but has no matching
    // row in execs/Top Performing Executives, making the totals disagree.
    convertedQuotationsAll.forEach(q => {
        const owner = resolveQuotationOwner(q);
        if (owner) execNamesSet.add(owner);
    });
    const convertedQuotationsForExec = (name) =>
        convertedQuotationsAll.filter((q) => resolveQuotationOwner(q) === name);

    const execs = [...execNamesSet].map(name => {
        const achieved = calcCollectedRevenue(name);
        const dealsWon = convertedQuotationsForExec(name).length;
        const target = targetByName[name] || 0;
        const achievement = target ? Number(Math.min((achieved / target) * 100, 100).toFixed(2)) : 0;
        return { name, target, achieved, dealsWon, achievement };
    }).sort((a, b) => b.achieved - a.achieved);

    const topPerformers = execs.slice(0, 5);
    const leaderboard = execs.slice(0, 3);

    // Recent Won Deals — value is the deal/quoted size, matching "Top Won Deals" /
    // the Report's logic, since this lists wins, not how much has been collected.
    const recentWon = convertedQuotationsForUser
        .map(q => {
            const lineItems = Array.isArray(q.line_items) ? q.line_items : [];
            const exec = resolveQuotationOwner(q) || '-';
            return {
                eq_id: q.qu_no,
                customer: q.customer_name || '-',
                project: lineItems.length ? lineItems[lineItems.length - 1].service_name : '-',
                value: Number(q.grand_total_saved || q.grand_total || 0),
                date: q.updated_on || q.date,
                exec,
            };
        })
        .filter(w => w.exec !== '-')
        .sort((a, b) => dayjs(b.date).valueOf() - dayjs(a.date).valueOf())
        .slice(0, 5);

    // ---- Follow-up summary ----
    const parseFU = (val) => { if (!val) return null; let d = dayjs(val, 'DD-MM-YYYY', true); if (!d.isValid()) d = dayjs(val); return d.isValid() ? d : null; };
    const todayStart = dayjs().startOf('day'), todayEnd = dayjs().endOf('day');
    const withFU = enquiries.map(d => ({ ...d, _fu: parseFU(d.enquiry_follow_up || d.n_follow_up) })).filter(d => d._fu);

    const followUp = {
        today: withFU.filter(d => d._fu.isBetween(todayStart, todayEnd, null, '[]')).length,
        tomorrow: withFU.filter(d => d._fu.isSame(dayjs().add(1, 'day'), 'day')).length,
        overdue: withFU.filter(d => d._fu.isBefore(todayStart)).length,
        completed: wonDeals,
    };

    const upcoming = withFU
        .filter(d => d._fu.isSameOrAfter(todayStart))
        .sort((a, b) => a._fu.valueOf() - b._fu.valueOf())
        .slice(0, 4)
        .map(d => {
            const services = parseItems(d.service_items);
            const assignedNames = (d.assigned_to || '').split(',').map(s => s.trim()).filter(Boolean);
            const creatorName = userMap[String(d.addedby ?? '').trim()];
            const followUpsArr = Array.isArray(d.follow_ups) ? d.follow_ups
                : (() => { try { return JSON.parse(d.follow_ups || '[]'); } catch { return []; } })();
            const latestFU = followUpsArr.length ? followUpsArr[followUpsArr.length - 1] : null;
            const fuType = latestFU?.follow_up_type || d.follow_up_type;
            return {
                eq_id: d.eq_id,
                date: d._fu.format('DD-MM-YYYY'),
                followUpType: fuType || d.eq_status || 'Follow-up',
                customer: d.name || '-',
                service: services.length ? services[services.length - 1].service_name : (d.service_name || '-'),
                exec: assignedNames.length ? assignedNames.join(', ') : (creatorName || '-'),
            };
        });

    const StatCard = ({ title, value, sub, color, bg, icon, link, linkLabel }) => (
        <Card bodyStyle={{ padding: '12px 14px' }} style={{ borderRadius: 10, border: '1px solid #f0f0f0', height: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                    <div style={{ fontSize: 11, color: '#888', marginBottom: 3 }}>{title}</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color, lineHeight: 1.2 }}>{value}</div>
                    <div style={{ fontSize: 10, color: '#888', marginTop: 4, display: 'flex', gap: 8, alignItems: 'center' }}>
                        <span>{sub}</span>
                        {link && <Button type="link" size="small" style={{ fontSize: 10, padding: 0, height: 'auto' }} onClick={link}>{linkLabel}</Button>}
                    </div>
                </div>
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: bg, color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>{icon}</div>
            </div>
        </Card>
    );
    if (!perm.read) {
        return (
            <Card bordered={false} style={{ margin: 24, textAlign: 'center', padding: 48 }}>

                <Typography.Text type="secondary">You don't have permission to view the Sales Dashboard.</Typography.Text>
            </Card>
        );
    }

    return (
        <Card bordered={false} bodyStyle={{ padding: 0 }}>
            <div style={{ padding: '10px 16px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <div>
                    <Typography.Title level={4} style={{ margin: 0, color: '#1658b4' }}>Sales Dashboard</Typography.Title>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>Dashboard &gt; Sales Dashboard</Typography.Text>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {/* <Select value={period} onChange={setPeriod} style={{ width: 130 }}
                        options={[{ value: 'Monthly', label: 'Monthly' }, { value: 'Quarterly', label: 'Quarterly' }, { value: 'Yearly', label: 'Yearly' }]} /> */}
                    <Button icon={<DownloadOutlined />}>Download</Button>
                    <Button icon={<FilterOutlined />}>Filter</Button>
                </div>
            </div>

            {/* Top Stat Cards */}
            <div style={{ padding: '12px 16px 0' }}>
                <Row gutter={[8, 8]}>
                    {topStats.map((c, i) => (
                        <Col xs={24} sm={12} md={8} lg={4} key={i}><StatCard {...c} /></Col>
                    ))}
                </Row>
            </div>

            {/* Trend + Pipeline + Source */}
            <div style={{ padding: '12px 16px 0' }}>
                <Row gutter={[8, 8]}>
                    <Col xs={24} lg={8}>
                        <Card style={{ borderRadius: 10, height: '100%' }} bodyStyle={{ padding: 12 }}>
                            <Typography.Text strong>Revenue Trend</Typography.Text>
                            <div style={{ width: '100%', height: 240, marginTop: 8 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={revenueTrend} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                        <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                                        <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `₹${(v / 1000).toFixed(0)}k`} />
                                        <RechartsTooltip formatter={v => inr(v)} contentStyle={{ fontSize: 11 }} />
                                        <Legend wrapperStyle={{ fontSize: 11 }} />
                                        <Line type="monotone" dataKey="achieved" name="Achieved Revenue" stroke="#1677ff" strokeWidth={2.5} dot={{ r: 3 }} />
                                        <Line type="monotone" dataKey="target" name="Target Revenue" stroke="#52c41a" strokeWidth={2.5} strokeDasharray="5 5" dot={{ r: 3 }} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </Card>
                    </Col>

                    <Col xs={24} lg={8}>
                        <Card style={{ borderRadius: 10, height: '100%' }} bodyStyle={{ padding: 12 }}>
                            <Typography.Text strong>Sales Pipeline (Overall)</Typography.Text>
                            <div style={{ marginTop: 10 }}>
                                {pipeline.map((p, i) => (
                                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
                                        <div style={{ flex: 1, position: 'relative', height: 22 }}>
                                            <div style={{ position: 'absolute', inset: 0, width: `${Math.max((p.count / pipelineMax) * 100, 24)}%`, background: `${p.color}2a`, borderRadius: 5 }} />
                                            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', height: '100%', padding: '0 10px', fontSize: 12, color: '#333' }}>{p.label}</div>
                                        </div>
                                        <span style={{ fontSize: 12, fontWeight: 700, color: '#222', minWidth: 28, textAlign: 'right' }}>{p.count}</span>
                                    </div>
                                ))}
                            </div>
                            <Divider style={{ margin: '8px 0' }} />
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                                <span style={{ color: '#888' }}>Conversion Rate</span>
                                <span style={{ fontWeight: 700, color: '#52c41a' }}>{conversionRate}%</span>
                            </div>
                        </Card>
                    </Col>

                    <Col xs={24} lg={8}>
                        <Card style={{ borderRadius: 10, height: '100%' }} bodyStyle={{ padding: 12 }}>
                            <Typography.Text strong>Revenue by Lead Source</Typography.Text>
                            {sourceData.length === 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 200, color: '#bbb' }}>
                                    <span style={{ fontSize: 28 }}>📊</span><span style={{ fontSize: 12, marginTop: 6 }}>No revenue data yet</span>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
                                    <div style={{ width: 140, height: 140, position: 'relative', flexShrink: 0 }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <RechartsTooltip formatter={(v, n, p) => [`${inr(v)} (${p.payload.pct}%)`, n]} contentStyle={{ fontSize: 11 }} />
                                                <Pie data={sourceData} dataKey="value" nameKey="name" innerRadius={42} outerRadius={66} paddingAngle={2}>
                                                    {sourceData.map((e, i) => <Cell key={i} fill={e.color} />)}
                                                </Pie>
                                            </PieChart>
                                        </ResponsiveContainer>
                                        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', textAlign: 'center' }}>
                                            <div style={{ fontSize: 13, fontWeight: 700 }}>{inr(sourceTotal)}</div>
                                            <div style={{ fontSize: 9, color: '#888' }}>Total Revenue</div>
                                        </div>
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        {sourceData.map((e, i) => (
                                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 5 }}>
                                                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ color: e.color }}>●</span><span style={{ color: '#333' }}>{e.name}</span></span>
                                                <span style={{ fontWeight: 600 }}>{inr(e.value)} ({e.pct}%)</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </Card>
                    </Col>
                </Row>
            </div>

            {/* Top Performers + Target Gauge + Recent Won */}
            <div style={{ padding: '12px 16px 0' }}>
                {(() => {
                    const myData = [{
                        name: currentUserName,
                        target: revenueTarget,
                        achieved: achievedRevenue,
                        dealsWon: wonDeals,
                        achievement: revenueTarget ? Number(Math.min((achievedRevenue / revenueTarget) * 100, 100).toFixed(2)) : 0,
                    }];
                    const tableColumns = [
                        { title: '#', key: 'i', width: 36, render: (_, __, i) => i + 1 },
                        { title: 'Executive', dataIndex: 'name' },
                        { title: 'Target (₹)', dataIndex: 'target', align: 'right', render: v => Number(v).toLocaleString('en-IN') },
                        { title: 'Achieved (₹)', dataIndex: 'achieved', align: 'right', render: v => Number(v).toLocaleString('en-IN') },
                        { title: 'Achievement', dataIndex: 'achievement', align: 'center', render: v => <Tag color={v >= 70 ? 'green' : v >= 40 ? 'orange' : 'red'}>{v.toFixed(2)}%</Tag> },
                    ];
                    const wonTableColumns = [
                        { title: 'Customer', dataIndex: 'customer', render: v => <span style={{ fontSize: 12 }}>{v}</span> },
                        { title: 'Project / Service', dataIndex: 'project', render: v => <span style={{ fontSize: 12, color: '#555' }}>{v}</span> },
                        { title: 'Deal Value', dataIndex: 'value', align: 'right', render: v => <span style={{ fontSize: 12, fontWeight: 700, color: '#722ed1' }}>₹{Number(v).toLocaleString('en-IN')}</span> },
                        { title: 'Won Date', dataIndex: 'date', align: 'center', render: v => <span style={{ fontSize: 12, color: '#52c41a' }}>{v ? dayjs(v).format('DD MMM YYYY') : '-'}</span> },
                        { title: 'Executive', dataIndex: 'exec', render: v => <span style={{ fontSize: 12 }}>{v}</span> },
                    ];
                    return (
                        <Row gutter={[8, 8]}>
                            <Col xs={24} lg={8}>
                                <Card style={{ borderRadius: 10, height: '100%' }} bodyStyle={{ padding: 12 }}
                                    title={<Typography.Text strong>{viewAll ? 'Top Performing Executives' : 'My Performance'}</Typography.Text>}
                                    extra={viewAll ? <Button type="link" size="small" onClick={() => navigate('/enquiry/sales-performance')}>View All</Button> : null}>
                                    {viewAll ? (
                                        <Table size="small" pagination={false} rowKey="name"
                                            dataSource={topPerformers}
                                            columns={tableColumns} />
                                    ) : (
                                        <>
                                            <div
                                                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '8px 0 4px', cursor: 'pointer' }}
                                                onClick={() => navigate('/enquiry/my-performance')}
                                            >
                                                <div style={{ width: 52, height: 52, borderRadius: '50%', background: '#f0f7ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, color: '#1677ff', fontWeight: 700, marginBottom: 8 }}>
                                                    {(myData[0]?.name || '?').charAt(0).toUpperCase()}
                                                </div>
                                                <div style={{ fontWeight: 700, fontSize: 14, color: '#222' }}>{myData[0]?.name}</div>
                                            </div>
                                            <Row gutter={[8, 8]} style={{ marginTop: 8 }} align="stretch">
                                                {[
                                                    { label: 'Target', value: inr(myData[0]?.target), color: '#1677ff', bg: '#f0f7ff' },
                                                    { label: 'Achieved', value: inr(myData[0]?.achieved), color: '#52c41a', bg: '#f6ffed' },
                                                    { label: 'Deals Won', value: myData[0]?.dealsWon, color: '#722ed1', bg: '#f9f0ff' },
                                                    { label: 'Achievement', value: `${myData[0]?.achievement.toFixed(2)}%`, color: '#fa8c16', bg: '#fff7e6' },
                                                ].map((s, i) => (
                                                    <Col span={12} key={i}>
                                                        <div style={{ background: s.bg, borderRadius: 8, padding: '10px 12px', textAlign: 'center' }}>
                                                            <div style={{ fontSize: 18, fontWeight: 700, color: s.color }}>{s.value}</div>
                                                            <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{s.label}</div>
                                                        </div>
                                                    </Col>
                                                ))}
                                            </Row>
                                        </>
                                    )}
                                </Card>
                            </Col>

                            <Col xs={24} lg={8}>
                                <Card style={{ borderRadius: 10, height: '100%' }} bodyStyle={{ padding: 12 }}>
                                    <Typography.Text strong>Target Achievement (Overall)</Typography.Text>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 12 }}>
                                        <div style={{ position: 'relative', width: 160, height: 160 }}>
                                            <div style={{
                                                width: 160, height: 160, borderRadius: '50%',
                                                background: `conic-gradient(#52c41a 0deg ${overallAchievement * 3.6}deg, #f0f0f0 ${overallAchievement * 3.6}deg 360deg)`,
                                            }} />
                                            <div style={{ position: 'absolute', top: 24, left: 24, width: 112, height: 112, borderRadius: '50%', background: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                                <div style={{ fontSize: 24, fontWeight: 700 }}>{overallAchievement.toFixed(2)}%</div>
                                                <div style={{ fontSize: 11, color: '#888' }}>Overall Achievement</div>
                                            </div>
                                        </div>
                                        <div style={{ width: '100%', marginTop: 16 }}>
                                            {[
                                                { label: 'Total Target', val: inr(revenueTarget), color: '#222' },
                                                { label: 'Achieved', val: inr(achievedRevenue), color: '#52c41a' },
                                                { label: 'Remaining', val: inr(Math.max(revenueTarget - achievedRevenue, 0)), color: '#f5222d' },
                                            ].map((s, i) => (
                                                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: i < 2 ? '1px solid #f5f5f5' : 'none' }}>
                                                    <Typography.Text type="secondary" style={{ fontSize: 13 }}>{s.label}</Typography.Text>
                                                    <Typography.Text strong style={{ fontSize: 13, color: s.color }}>{s.val}</Typography.Text>
                                                </div>
                                            ))}
                                        </div>
                                        <Button type="link" onClick={() => navigate('/sales-target')} style={{ marginTop: 6 }}>View Target Details</Button>
                                    </div>
                                </Card>
                            </Col>

                            <Col xs={24} lg={8}>
                                <Card style={{ borderRadius: 10, height: '100%' }} bodyStyle={{ padding: 12 }}
                                    title={<Typography.Text strong>Recent Won Deals</Typography.Text>}
                                    extra={<Button type="link" size="small" onClick={() => navigate('/enquiry')}>View All</Button>}>
                                    <Table size="small" pagination={false} rowKey="eq_id"
                                        dataSource={recentWon}
                                        locale={{ emptyText: 'No won deals yet' }}
                                        columns={wonTableColumns} />
                                </Card>
                            </Col>
                        </Row>
                    );
                })()}
            </div>

            {/* Follow-up Summary + Upcoming + Leaderboard */}
            <div style={{ padding: '12px 16px 16px' }}>
                <Row gutter={[8, 8]}>
                    <Col xs={24} lg={6}>
                        <Card style={{ borderRadius: 10, height: '100%' }} bodyStyle={{ padding: 12 }}>
                            <Typography.Text strong>Follow-up Summary</Typography.Text>
                            <Row gutter={[8, 8]} style={{ marginTop: 10 }}>
                                {[
                                    { label: 'Due Today', val: followUp.today, color: '#1677ff', bg: '#f0f7ff', icon: <CalendarOutlined /> },
                                    { label: 'Due Tomorrow', val: followUp.tomorrow, color: '#13c2c2', bg: '#f0fffe', icon: <ClockCircleOutlined /> },
                                    { label: 'Overdue', val: followUp.overdue, color: '#f5222d', bg: '#fff1f0', icon: <ExclamationCircleOutlined /> },
                                    { label: 'Completed', val: followUp.completed, color: '#52c41a', bg: '#f6ffed', icon: <CheckCircleOutlined /> },
                                ].map((s, i) => (
                                    <Col span={12} key={i}>
                                        <div style={{ background: s.bg, borderRadius: 8, padding: '10px', textAlign: 'center' }}>
                                            <div style={{ color: s.color, fontSize: 18 }}>{s.icon}</div>
                                            <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{s.label}</div>
                                            <div style={{ fontSize: 20, fontWeight: 700, color: s.color }}>{s.val}</div>
                                        </div>
                                    </Col>
                                ))}
                            </Row>
                        </Card>
                    </Col>

                    <Col xs={24} lg={10}>
                        <Card style={{ borderRadius: 10, height: '100%' }} bodyStyle={{ padding: 12 }}
                            title={<Typography.Text strong>Upcoming Activities</Typography.Text>}
                            extra={<Button type="link" size="small" onClick={() => navigate('/enquiry')}>View Calendar</Button>}>
                            {upcoming.length === 0 ? (
                                <Typography.Text type="secondary" style={{ fontSize: 12 }}>No upcoming activities</Typography.Text>
                            ) : upcoming.map((a, i) => {
                                const dotColor = i === 0 ? '#722ed1' : i === 1 ? '#52c41a' : i === 2 ? '#fa8c16' : '#bfbfbf';
                                return (
                                    <div key={a.eq_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: i < upcoming.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                                        <div style={{ minWidth: 72, textAlign: 'center', background: '#f0f7ff', borderRadius: 8, padding: '3px 6px' }}>
                                            <div style={{ fontSize: 11, fontWeight: 700, color: '#1677ff' }}>{a.date}</div>
                                        </div>
                                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: dotColor, flexShrink: 0 }} />
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontSize: 12, fontWeight: 700, color: '#222' }}>{a.followUpType}</div>
                                            <div style={{ fontSize: 11, color: '#555', fontWeight: 500 }}>{a.customer}</div>
                                        </div>
                                        <div style={{ fontSize: 12, color: '#333', fontWeight: 500, flexShrink: 0, textAlign: 'right', minWidth: 90 }}>
                                            {a.exec}
                                        </div>
                                    </div>
                                );
                            })}
                        </Card>
                    </Col>

                    <Col xs={24} lg={8}>
                        <Card style={{ borderRadius: 10, height: '100%' }} bodyStyle={{ padding: 12 }}
                            title={<Typography.Text strong>Sales Leaderboard</Typography.Text>}
                            extra={viewAll ? <Button type="link" size="small" onClick={() => navigate('/enquiry/sales-performance')}>View All</Button> : null}>
                            <Table size="small" pagination={false} rowKey="name" dataSource={leaderboard}
                                columns={[
                                    { title: 'Rank', key: 'r', width: 50, render: (_, __, i) => ['🥇', '🥈', '🥉'][i] || (i + 1) },
                                    { title: 'Executive', dataIndex: 'name' },
                                    { title: 'Achieved (₹)', dataIndex: 'achieved', align: 'right', render: v => Number(v).toLocaleString('en-IN') },
                                    { title: 'Deals Won', dataIndex: 'dealsWon', align: 'center' },
                                    { title: 'Achievement', dataIndex: 'achievement', align: 'center', render: v => <Tag color={v >= 70 ? 'green' : v >= 40 ? 'orange' : 'red'}>{v.toFixed(2)}%</Tag> },
                                ]} />
                        </Card>
                    </Col>
                </Row>
            </div>
        </Card>
    );
};

export default SalesDashboard;